
import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import LazyImage from '@/components/ui/LazyImage';
import ImageWithSchema from '@/components/ui/ImageWithSchema';
import { Droplets, Wind, Sparkles, Filter, Sun, Brush, Trash2 } from 'lucide-react';
import gutterCleaning1 from '@/assets/gutter-cleaning-1.webp';
import gutterCleaning2 from '@/assets/gutter-cleaning-2.webp';
import solarCleaning1 from '@/assets/solar-cleaning-ground-pole.webp';
import solarCleaning2 from '@/assets/solar-cleaning-2.webp';
import softWash1 from '@/assets/soft-wash-1.webp';
import softWash2 from '@/assets/soft-wash-2.webp';
import dumpsterPadBefore from '@/assets/dumpster-pad-before-1.jpg';
import dumpsterPadAfter from '@/assets/dumpster-pad-after-1.jpg';

// This would typically come from a CMS or API
const servicesData = [
  {
    id: 1,
    title: "Pressure Washing",
    description: "Remove grime, mold, and dirt from your home's exterior for a refreshed look.",
    icon: <Droplets className="h-10 w-10 text-ventura-blue" />,
    beforeImage: "/lovable-uploads/e57635df-c5ff-463b-a685-e793219f7fff.webp",
    afterImage: "/lovable-uploads/a0c5a82a-7156-4024-be0d-894ad3f62b01.webp",
    beforeAlt: "Dirty stained concrete driveway before eco-friendly pressure washing service in Ventura County",
    afterAlt: "Spotless clean concrete driveway after professional pressure washing in Ventura CA",
    link: "/services/pressure-washing"
  },
  {
    id: 2,
    title: "Window Washing",
    description: "Achieve streak-free windows that enhance curb appeal.",
    icon: <Sparkles className="h-10 w-10 text-ventura-blue" />,
    beforeImage: "/lovable-uploads/50da41c5-f891-4836-974c-772b27c05f1a.webp",
    afterImage: "/lovable-uploads/862089e8-1263-474b-8de2-ba77e319b7af.webp",
    beforeAlt: "Dirty streaked windows with water spots before professional window cleaning in Camarillo",
    afterAlt: "Crystal clear streak-free windows after eco-friendly window washing in Ventura County",
    link: "/services/window-washing"
  },
  {
    id: 3,
    title: "Carpet Cleaning",
    description: "Deep-clean technology for fresher, healthier carpets.",
    icon: <Wind className="h-10 w-10 text-ventura-blue" />,
    beforeImage: "/lovable-uploads/41a0177b-af69-4c82-90f1-192ad6d826ce.webp",
    afterImage: "/lovable-uploads/66ba87fc-3ed0-4ed4-a370-6afea398d84c.webp",
    beforeAlt: "Heavily stained carpet with pet odors before professional deep cleaning in Thousand Oaks",
    afterAlt: "Fresh sanitized carpet after eco-friendly deep cleaning service in Ventura County",
    link: "/services/carpet-cleaning"
  },
  {
    id: 4,
    title: "Gutter Cleaning",
    description: "Clear debris to protect your Ventura home.",
    icon: <Filter className="h-10 w-10 text-ventura-blue" />,
    beforeImage: gutterCleaning1,
    afterImage: gutterCleaning2,
    beforeAlt: "Clogged rain gutters full of leaves and debris before professional gutter cleaning in Oxnard",
    afterAlt: "Clean flowing gutters after thorough professional gutter maintenance in Ventura County",
    link: "/services/gutter-cleaning"
  },
  {
    id: 5,
    title: "Solar Panel Cleaning",
    description: "Boost solar efficiency with eco-friendly methods.",
    icon: <Sun className="h-10 w-10 text-ventura-blue" />,
    beforeImage: solarCleaning1,
    afterImage: solarCleaning2,
    beforeAlt: "Dirty dusty solar panels with reduced efficiency before eco-friendly cleaning in Simi Valley",
    afterAlt: "Spotless solar panels maximizing energy output after professional cleaning in Ventura CA",
    link: "/services/solar-panel-cleaning"
  },
  {
    id: 6,
    title: "Soft Wash",
    description: "Brighten the exterior of your home and clear your roof of organic matter.",
    icon: <Brush className="h-10 w-10 text-ventura-blue" />,
    beforeImage: softWash1,
    afterImage: softWash2,
    beforeAlt: "Algae and mildew buildup on house siding before gentle eco-friendly soft wash in Moorpark",
    afterAlt: "Bright clean home exterior after professional soft washing service in Ventura County",
    link: "/services/soft-wash"
  },
  {
    id: 7,
    title: "Dumpster Pad Cleaning",
    description: "Degreasing and sanitizing for restaurants, HOAs, and commercial properties.",
    icon: <Trash2 className="h-10 w-10 text-ventura-blue" />,
    beforeImage: dumpsterPadBefore,
    afterImage: dumpsterPadAfter,
    beforeAlt: "Grease-stained dumpster interior before professional degreasing in Ventura County",
    afterAlt: "Clean dumpster pad area after professional cleaning service in Ventura CA",
    link: "/services/dumpster-pad-cleaning"
  }
];

const Services = () => {
  return (
    <section id="services" className="section-padding bg-white">
      <div className="container mx-auto">
        <h2 className="section-title">Our Professional Services</h2>
        <p className="section-subtitle">
          Transform your home or business with our premium cleaning services
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
          {servicesData.map((service) => (
            <Link key={service.id} to={service.link} className="block group h-full">
              <Card className="card-shadow rounded-xl overflow-hidden transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-1 h-full flex flex-col border-t-4 border-t-ventura-blue">
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex justify-center mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-center mb-4 group-hover:text-ventura-blue transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-center mb-6">{service.description}</p>
                  
                  <ImageWithSchema 
                    src={service.afterImage} 
                    alt={service.afterAlt} 
                    className="mt-auto w-full h-48 object-cover rounded-lg"
                    imageName={`${service.title} - Professional Results`}
                    imageDescription={service.afterAlt}
                    serviceType={service.title}
                    beforeAfter="after"
                  />
                  
                  <div className="mt-4 text-center">
                    <span className="text-ventura-blue font-semibold group-hover:underline">
                      Learn More →
                    </span>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
